import { useState, useMemo, useEffect } from 'react';
import {
    AlertCircle,
    Banknote,
    Bitcoin,
    CheckCircle,
    CreditCard,
    DollarSign,
    Link2,
    Loader2,
    Plus,
    Power,
    PowerOff,
    RefreshCw,
    Settings,
    Trash2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { GenericDataTable } from '@/components/shared/tables';
import { Column } from '@/components/ui/data-table';
import { useSnackbar } from 'notistack';
import { PageContainer, PageHeader, StatTile, StatTileGrid } from '@/components/shared/layout';
import { EmptyState } from '@/components/shared/EmptyState';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { formatCurrency } from '@/lib/format';
import {
    type PaymentMethod,
    type PaymentMethodCreateRequest,
    type ManualCurrencyConfigPayload,
    type ManualDetailType,
    useAvailableCurrencies,
    useAvailableProcessors,
    useCreatePaymentMethod,
    useDeletePaymentMethod,
    usePaymentMethodsList,
    usePaymentMethodsStatistics,
    useTogglePaymentMethodStatus,
    useUpdatePaymentMethod,
    useUpdatePaymentMethodProcessors,
    usePaymentMethodProcessors,
} from '@/api/payment-methods/use-payment-methods';
import { useUrlState } from '@/hooks/shared';
import { usePermissions } from '@/hooks/usePermissions';
import { ModulePermission, PermissionAction } from '@/types/permissions';

type PaymentMethodTypeUi = 'processor' | 'manual';

interface ManualRowState {
    currency_id: string;
    detail_type: ManualDetailType;
    bank_name: string;
    iban: string;
    account_number: string;
    swift: string;
    branch_address: string;
    wallet_address: string;
    network: string;
    memo: string;
    card_holder: string;
    card_number: string;
    instructions: string;
}

function defaultManualRow(): ManualRowState {
    return {
        currency_id: '',
        detail_type: 'bank',
        bank_name: '',
        iban: '',
        account_number: '',
        swift: '',
        branch_address: '',
        wallet_address: '',
        network: '',
        memo: '',
        card_holder: '',
        card_number: '',
        instructions: '',
    };
}

function manualConfigToRow(config: ManualCurrencyConfigPayload): ManualRowState {
    const d = (config.details || {}) as Record<string, string>;
    const row = defaultManualRow();
    row.currency_id = config.currency_id;
    row.detail_type = config.detail_type;
    if (config.detail_type === 'bank') {
        row.bank_name = d.bank_name || '';
        row.iban = d.iban || '';
        row.account_number = d.account_number || '';
        row.swift = d.swift || '';
        row.branch_address = d.branch_address || '';
    } else if (config.detail_type === 'crypto') {
        row.wallet_address = d.wallet_address || '';
        row.network = d.network || '';
        row.memo = d.memo || '';
    } else if (config.detail_type === 'credit_card') {
        row.card_holder = d.card_holder || '';
        row.card_number = d.card_number || '';
    } else {
        row.instructions = d.instructions || d.text || '';
    }
    return row;
}

function rowToDetails(row: ManualRowState): Record<string, unknown> {
    if (row.detail_type === 'bank') {
        return {
            bank_name: row.bank_name,
            iban: row.iban || undefined,
            account_number: row.account_number || undefined,
            swift: row.swift || undefined,
            branch_address: row.branch_address || undefined,
        };
    }
    if (row.detail_type === 'crypto') {
        return {
            wallet_address: row.wallet_address,
            network: row.network || undefined,
            memo: row.memo || undefined,
        };
    }
    if (row.detail_type === 'credit_card') {
        return {
            card_holder: row.card_holder,
            card_number: row.card_number,
        };
    }
    return { instructions: row.instructions };
}

function buildManualCurrencyConfigs(manualRows: ManualRowState[]): ManualCurrencyConfigPayload[] {
    return manualRows
        .filter(r => !!r.currency_id)
        .map((row, idx) => ({
            currency_id: row.currency_id,
            detail_type: row.detail_type,
            details: rowToDetails(row),
            sort_order: idx,
        }));
}

interface PaymentMethodFormData {
    name: string;
    code: string;
    display_name: string;
    description: string;
    note: string;
    requires_address: boolean;
    requires_card_details: boolean;
    requires_bank_details: boolean;
    method_category: 'fiat' | 'crypto';
    is_active: boolean;
    method_type: PaymentMethodTypeUi;
    supports_processors: boolean;
    processor_config: any;
    processing_time_hours: number;
    min_amount: number;
    max_amount: number;
    fee_percentage: number;
    fee_fixed: number;
    metadata: any;
}

const initialFormData: PaymentMethodFormData = {
    name: '',
    code: '',
    display_name: '',
    description: '',
    note: '',
    requires_address: false,
    requires_card_details: false,
    requires_bank_details: false,
    method_category: 'fiat',
    is_active: true,
    method_type: 'processor',
    supports_processors: false,
    processor_config: {},
    processing_time_hours: 24,
    min_amount: 0,
    max_amount: 10000,
    fee_percentage: 0,
    fee_fixed: 0,
    metadata: {},
};

export default function PaymentMethodsPage() {
    const { hasPermission } = usePermissions();
    const { enqueueSnackbar } = useSnackbar();

    // Use URL-based state management
    const {
        filters,
        sort,
        pagination,
        updateFilters,
        clearFilters,
        toggleSort,
        goToPage,
        updatePagination,
    } = useUrlState({
        sort: { key: 'name', direction: 'asc' },
        pagination: { page: 1, pageSize: 25 },
    });

    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [isCreateMode, setIsCreateMode] = useState(false);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<PaymentMethod | null>(null);
    const [formData, setFormData] = useState<PaymentMethodFormData>(initialFormData);
    const [manualRows, setManualRows] = useState<ManualRowState[]>([]);
    const [processorConfigs, setProcessorConfigs] = useState<
        Record<string, { is_enabled: boolean; processor_settings: any }>
    >({});

    // Queries - only pass filters that have values, like LeadsPage
    const {
        data: paymentMethodsData,
        isLoading,
        error,
    } = usePaymentMethodsList({
        page: pagination.page,
        per_page: pagination.pageSize,
        sort_by: sort.key,
        sort_order: sort.direction,
        ...Object.fromEntries(
            Object.entries(filters).filter(
                ([, value]) =>
                    value !== undefined && value !== '' && value !== null && value !== 'all'
            )
        ),
    });
    const { data: currencies } = useAvailableCurrencies();
    const allowedSymbols = useMemo(
        () =>
            formData.method_category === 'crypto'
                ? ['USDT', 'USDC', 'EURC', 'EURT']
                : ['USD', 'EUR'],
        [formData.method_category]
    );
    const filteredCurrencies = useMemo(
        () =>
            (currencies || []).filter((c: any) =>
                allowedSymbols.includes(String(c.symbol || '').toUpperCase())
            ),
        [currencies, allowedSymbols]
    );
    useEffect(() => {
        if (!currencies?.length) return;
        const allowedIds = new Set(
            currencies
                .filter((c: any) => allowedSymbols.includes(String(c.symbol || '').toUpperCase()))
                .map((c: any) => c.id)
        );
        setManualRows(prev => prev.filter(row => allowedIds.has(row.currency_id)));
    }, [allowedSymbols, currencies]);
    const { data: statistics, isLoading: isLoadingStatistics } = usePaymentMethodsStatistics();
    const { data: currentProcessors } = usePaymentMethodProcessors(selectedPaymentMethod?.id || '');
    const { data: availableProcessors } = useAvailableProcessors();
    const updateProcessors = useUpdatePaymentMethodProcessors();

    // Sync processor configs when dialog opens with a payment method
    useEffect(() => {
        if (!isDialogOpen || !selectedPaymentMethod || !currentProcessors || !availableProcessors)
            return;
        const configs: Record<string, { is_enabled: boolean; processor_settings: any }> = {};
        currentProcessors.forEach(proc => {
            configs[proc.tenant_integration_id] = {
                is_enabled: proc.is_enabled,
                processor_settings: proc.processor_settings || {},
            };
        });
        availableProcessors.forEach(proc => {
            if (!configs[proc.id]) {
                configs[proc.id] = { is_enabled: false, processor_settings: {} };
            }
        });
        setProcessorConfigs(configs);
    }, [isDialogOpen, selectedPaymentMethod?.id, currentProcessors, availableProcessors]);

    // Mutations
    const createPaymentMethod = useCreatePaymentMethod();
    const updatePaymentMethod = useUpdatePaymentMethod();
    const deletePaymentMethod = useDeletePaymentMethod();
    const toggleStatus = useTogglePaymentMethodStatus();

    const paymentMethods = paymentMethodsData?.data || [];
    const paginationData = paymentMethodsData?.pagination;

    // Handlers
    const handleFilterChange = (newFilters: any) => {
        updateFilters(newFilters);
    };

    const handleClearFilters = () => {
        clearFilters();
    };

    /** Drives the empty state: "no matches" reads differently from "nothing yet". */
    const hasActiveFilters = useMemo(
        () => Object.values(filters ?? {}).some(v => v !== '' && v !== null && v !== undefined),
        [filters]
    );

    const handleSort = (key: keyof PaymentMethod) => {
        toggleSort(key as string);
    };

    const handlePageSizeChange = (newPageSize: number) => {
        updatePagination({ page: 1, pageSize: newPageSize });
    };

    // Define filter fields for the GenericDataTable
    const filterFields = useMemo(
        () => [
            {
                key: 'search',
                label: 'Search',
                type: 'text' as const,
                placeholder: 'Search payment methods...',
            },
            {
                key: 'is_active',
                label: 'Status',
                type: 'select' as const,
                options: [
                    { value: 'all', label: 'All Statuses' },
                    { value: 'true', label: 'Active' },
                    { value: 'false', label: 'Inactive' },
                ],
            },
            {
                key: 'method_category',
                label: 'Category',
                type: 'select' as const,
                options: [
                    { value: 'all', label: 'All Categories' },
                    { value: 'fiat', label: 'Fiat' },
                    { value: 'crypto', label: 'Crypto' },
                ],
            },
        ],
        []
    );

    // Define columns for the GenericDataTable
    const columns: Column<PaymentMethod>[] = useMemo(
        () => [
            {
                id: 'method',
                label: 'Method',
                key: 'display_name',
                sortable: true,
                width: '200px',
                render: (_value, paymentMethod) => (
                    <div className="flex items-center space-x-3">
                        {getTypeIcon(paymentMethod)}
                        <div>
                            <div className="font-medium">{paymentMethod.display_name}</div>
                            <div className="text-sm text-muted-foreground">
                                {paymentMethod.code}
                            </div>
                        </div>
                    </div>
                ),
            },
            {
                id: 'type',
                label: 'Type',
                key: 'method_category',
                sortable: true,
                width: '100px',
                render: (_value, paymentMethod) => getTypeBadge(paymentMethod),
            },
            {
                id: 'kind',
                label: 'Kind',
                key: 'method_type',
                sortable: false,
                width: '120px',
                render: (_value, paymentMethod) => getMethodKindBadge(paymentMethod),
            },
            {
                id: 'status',
                label: 'Status',
                key: 'is_active',
                sortable: true,
                width: '100px',
                render: (_value, paymentMethod) => (
                    <StatusBadge
                        status={paymentMethod.is_active ? 'active' : 'inactive'}
                        kind="account"
                    />
                ),
            },
            {
                id: 'processing_time',
                label: 'Processing Time',
                key: 'processing_time_hours',
                sortable: true,
                width: '150px',
                render: (_value, paymentMethod) => (
                    <span className="text-sm">{paymentMethod.processing_time_description}</span>
                ),
            },
            {
                id: 'amount_limits',
                label: 'Min/Max Amount',
                key: 'min_amount',
                sortable: true,
                width: '150px',
                render: (_value, paymentMethod) => (
                    <div className="text-sm tabular-figures">
                        <div>Min: {formatCurrency(paymentMethod.min_amount)}</div>
                        <div>Max: {formatCurrency(paymentMethod.max_amount)}</div>
                    </div>
                ),
            },
            {
                id: 'fees',
                label: 'Fees',
                key: 'fee_percentage',
                sortable: true,
                width: '120px',
                render: (_value, paymentMethod) => (
                    <div className="text-sm tabular-figures">
                        {paymentMethod.fee_percentage > 0 && (
                            <div>{paymentMethod.fee_percentage}%</div>
                        )}
                        {paymentMethod.fee_fixed > 0 && (
                            <div>{formatCurrency(paymentMethod.fee_fixed)} fixed</div>
                        )}
                        {paymentMethod.fee_percentage === 0 && paymentMethod.fee_fixed === 0 && (
                            <div className="text-success">No fees</div>
                        )}
                    </div>
                ),
            },
            {
                id: 'currencies',
                label: 'Currencies',
                key: 'supported_currency_symbols',
                sortable: false,
                width: '150px',
                render: (_value, paymentMethod) => (
                    <div className="flex flex-wrap gap-1">
                        {paymentMethod.supported_currency_symbols.slice(0, 3).map(symbol => (
                            <Badge key={symbol} variant="outline" className="text-xs">
                                {symbol}
                            </Badge>
                        ))}
                        {paymentMethod.supported_currency_symbols.length > 3 && (
                            <Badge variant="outline" className="text-xs">
                                +{paymentMethod.supported_currency_symbols.length - 3}
                            </Badge>
                        )}
                    </div>
                ),
            },
        ],
        []
    );

    const handleCreate = () => {
        setSelectedPaymentMethod(null);
        setFormData(initialFormData);
        setManualRows([]);
        setProcessorConfigs({});
        setIsCreateMode(true);
        setIsDialogOpen(true);
    };

    const handleEdit = (paymentMethod: PaymentMethod) => {
        setSelectedPaymentMethod(paymentMethod);
        setManualRows((paymentMethod.manual_currency_configs || []).map(c => manualConfigToRow(c)));
        setFormData({
            name: paymentMethod.name,
            code: paymentMethod.code,
            display_name: paymentMethod.display_name,
            description: paymentMethod.description || '',
            note: paymentMethod.note || '',
            requires_address: paymentMethod.requires_address,
            requires_card_details: paymentMethod.requires_card_details,
            requires_bank_details: paymentMethod.requires_bank_details ?? false,
            method_category: paymentMethod.method_category || 'fiat',
            is_active: paymentMethod.is_active,
            method_type: (paymentMethod.method_type as PaymentMethodTypeUi) || 'processor',
            supports_processors: paymentMethod.supports_processors || false,
            processor_config: paymentMethod.processor_config || {},
            processing_time_hours: paymentMethod.processing_time_hours,
            min_amount: paymentMethod.min_amount,
            max_amount: paymentMethod.max_amount,
            fee_percentage: paymentMethod.fee_percentage,
            fee_fixed: paymentMethod.fee_fixed,
            metadata: paymentMethod.metadata || {},
        });
        setIsCreateMode(false);
        setIsDialogOpen(true);
    };

    const handleDelete = (paymentMethod: PaymentMethod) => {
        setSelectedPaymentMethod(paymentMethod);
        setIsDeleteDialogOpen(true);
    };

    const handleToggleProcessor = (integrationId: string, enabled: boolean) => {
        setProcessorConfigs(prev => ({
            ...prev,
            [integrationId]: {
                ...prev[integrationId],
                is_enabled: enabled,
            },
        }));
    };

    const handleSaveProcessors = async () => {
        if (!selectedPaymentMethod?.id) return;
        try {
            const processor_configs = Object.entries(processorConfigs).map(
                ([tenant_integration_id, config]) => ({
                    tenant_integration_id,
                    is_enabled: config.is_enabled,
                    processor_settings: config.processor_settings,
                })
            );
            await updateProcessors.mutateAsync({
                paymentMethodId: selectedPaymentMethod.id,
                data: { processor_configs },
            });
            enqueueSnackbar('Processors updated successfully', { variant: 'success' });
        } catch (error: any) {
            enqueueSnackbar(error?.response?.data?.message || 'Failed to update processors', {
                variant: 'error',
            });
        }
    };

    const handleToggleStatus = async (id: string) => {
        try {
            const result = await toggleStatus.mutateAsync(id);
            enqueueSnackbar('Payment method status updated successfully.', { variant: 'success' });
            if (result?.is_active !== undefined && selectedPaymentMethod?.id === id) {
                setFormData(prev => ({ ...prev, is_active: result.is_active }));
                setSelectedPaymentMethod(prev =>
                    prev ? { ...prev, is_active: result.is_active } : null
                );
            }
        } catch (error) {
            enqueueSnackbar('Failed to update payment method status.', { variant: 'error' });
        }
    };

    const getApiErrorMessage = (error: any, fallback: string): string => {
        const data = error?.response?.data;
        const validationErrors = data?.errors as Record<string, string[] | undefined> | undefined;
        if (validationErrors && typeof validationErrors === 'object') {
            const firstMessages = Object.values(validationErrors)
                .flatMap(v => (Array.isArray(v) ? v : []))
                .filter(Boolean);
            if (firstMessages.length > 0) {
                return firstMessages[0];
            }
        }
        return data?.message || error?.message || fallback;
    };

    const handleSubmit = async () => {
        try {
            const supportsProcessors =
                formData.method_type === 'processor' ? formData.supports_processors : false;
            const manualConfigs = buildManualCurrencyConfigs(manualRows);

            const payload: PaymentMethodCreateRequest = {
                ...formData,
                requires_bank_details: false,
                requires_card_details: false,
                supports_processors: supportsProcessors,
                manual_currency_configs: manualConfigs,
            };

            if (isCreateMode) {
                const created = await createPaymentMethod.mutateAsync(payload);
                enqueueSnackbar('Payment method created successfully.', { variant: 'success' });
                if (created?.id) {
                    setSelectedPaymentMethod(created as PaymentMethod);
                    setIsCreateMode(false);
                } else {
                    setIsDialogOpen(false);
                    setFormData(initialFormData);
                }
            } else if (selectedPaymentMethod) {
                await updatePaymentMethod.mutateAsync({
                    id: selectedPaymentMethod.id,
                    ...payload,
                });
                enqueueSnackbar('Payment method updated successfully.', { variant: 'success' });
                setIsDialogOpen(false);
                setFormData(initialFormData);
                setSelectedPaymentMethod(null);
            }
        } catch (error: any) {
            enqueueSnackbar(getApiErrorMessage(error, 'Failed to save payment method.'), {
                variant: 'error',
            });
        }
    };

    const handleConfirmDelete = async () => {
        if (!selectedPaymentMethod) return;

        try {
            await deletePaymentMethod.mutateAsync(selectedPaymentMethod.id);
            enqueueSnackbar('Payment method has been deleted successfully.', {
                variant: 'success',
            });
            setIsDeleteDialogOpen(false);
            setSelectedPaymentMethod(null);
        } catch (error: any) {
            enqueueSnackbar(getApiErrorMessage(error, 'Failed to delete payment method.'), {
                variant: 'error',
            });
        }
    };

    const getTypeIcon = (paymentMethod: PaymentMethod) => {
        if (paymentMethod.method_category === 'crypto') return <Bitcoin className="h-4 w-4" />;
        if (paymentMethod.requires_card_details) return <CreditCard className="h-4 w-4" />;
        return <Banknote className="h-4 w-4" />;
    };

    const getTypeBadge = (paymentMethod: PaymentMethod) => {
        if (paymentMethod.method_category === 'crypto') {
            return (
                <Badge className="border border-warning/30 bg-warning/10 text-warning">
                    <Bitcoin className="h-3 w-3" />
                    Crypto
                </Badge>
            );
        }
        return (
            <Badge className="border border-info/30 bg-info/10 text-info">
                <DollarSign className="h-3 w-3" />
                Fiat
            </Badge>
        );
    };

    const getMethodKindBadge = (paymentMethod: PaymentMethod) => {
        const mt =
            paymentMethod.method_type ||
            (paymentMethod.supports_processors ? 'processor' : 'manual');
        if (mt === 'processor') {
            return (
                <Badge className="border border-primary/30 bg-primary/10 text-primary">
                    <Link2 className="h-3 w-3" />
                    Processor
                </Badge>
            );
        }
        return (
            <Badge className="border border-warning/30 bg-warning/10 text-warning">
                <Banknote className="h-3 w-3" />
                Manual
            </Badge>
        );
    };

    if (error) {
        return (
            <div className="flex min-h-[400px] items-center justify-center">
                <div className="text-center">
                    <AlertCircle className="mx-auto mb-4 h-12 w-12 text-destructive" />
                    <h3 className="mb-2 text-lg font-semibold">Error Loading Payment Methods</h3>
                    <p className="mb-4 text-muted-foreground">
                        Failed to load payment methods data.
                    </p>
                    <Button onClick={() => window.location.reload()}>
                        <RefreshCw className="h-4 w-4" />
                        Retry
                    </Button>
                </div>
            </div>
        );
    }

    return (
        <PageContainer fullHeight>
            <PageHeader
                title="Payment Methods"
                description="Manage payment methods for deposits and withdrawals"
                actions={
                    hasPermission(ModulePermission.PAYMENT_METHODS, PermissionAction.CREATE) ? (
                        <Button onClick={handleCreate}>
                            <Plus className="h-4 w-4" />
                            Add Payment Method
                        </Button>
                    ) : undefined
                }
            />

            <StatTileGrid className="shrink-0">
                <StatTile
                    label="Total Methods"
                    value={statistics?.total ?? 0}
                    icon={Settings}
                    tone="brand"
                    isLoading={isLoadingStatistics}
                />
                <StatTile
                    label="Active"
                    value={statistics?.active ?? 0}
                    icon={CheckCircle}
                    tone="success"
                    isLoading={isLoadingStatistics}
                />
                <StatTile
                    label="Fiat Methods"
                    value={statistics?.fiat ?? 0}
                    icon={DollarSign}
                    tone="info"
                    isLoading={isLoadingStatistics}
                />
                <StatTile
                    label="Crypto Methods"
                    value={statistics?.crypto ?? 0}
                    icon={Bitcoin}
                    tone="warning"
                    isLoading={isLoadingStatistics}
                />
            </StatTileGrid>

            {/* Generic Data Table */}
            <GenericDataTable<PaymentMethod>
                data={paymentMethods}
                columns={columns}
                visibleColumns={columns.map(col => col.id)}
                filters={filters}
                filterFields={filterFields}
                presets={[]}
                isLoading={isLoading}
                selectedItems={[]}
                onSelectionChange={() => {}}
                getItemId={paymentMethod => paymentMethod.id}
                sortConfig={{
                    key: sort.key as keyof PaymentMethod,
                    direction: sort.direction,
                }}
                onSort={handleSort}
                totalItems={paginationData?.total || paymentMethods.length}
                totalPages={paginationData?.last_page || 1}
                currentPage={pagination.page}
                pageSize={pagination.pageSize}
                onPageChange={goToPage}
                onPageSizeChange={handlePageSizeChange}
                onRowClick={handleEdit}
                onFilterChange={handleFilterChange}
                onClearFilters={handleClearFilters}
                onLoadPreset={() => {}}
                onSavePreset={() => {}}
                onColumnToggle={() => {}}
                onResetColumns={() => {}}
                emptyMessage="No payment methods found"
                loadingMessage="Loading payment methods..."
                showExportButton={true}
                showRowClick={true}
                stickyLayout
                emptyState={
                    <EmptyState
                        icon={Settings}
                        title="No payment methods found"
                        description={
                            hasActiveFilters
                                ? 'No payment method matches the current filters. Try clearing them.'
                                : 'Payment methods will appear here once you add one.'
                        }
                        action={
                            hasActiveFilters ? (
                                <Button variant="outline" size="sm" onClick={handleClearFilters}>
                                    Clear filters
                                </Button>
                            ) : hasPermission(
                                  ModulePermission.PAYMENT_METHODS,
                                  PermissionAction.CREATE
                              ) ? (
                                <Button size="sm" onClick={handleCreate}>
                                    <Plus className="h-4 w-4" />
                                    Add Payment Method
                                </Button>
                            ) : undefined
                        }
                    />
                }
            />

            {/* Create/Edit Dialog */}
            <Dialog open={isDialogOpen} onOpenChange={open => !open && setIsDialogOpen(false)}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>
                            {isCreateMode ? 'Create Payment Method' : 'Edit Payment Method'}
                        </DialogTitle>
                        <DialogDescription>
                            {isCreateMode
                                ? 'Add a new payment method for deposits and withdrawals.'
                                : 'Update the payment method details below.'}
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="name">Name *</Label>
                                <Input
                                    id="name"
                                    value={formData.name}
                                    onChange={e =>
                                        setFormData({ ...formData, name: e.target.value })
                                    }
                                    placeholder="e.g., Bank Transfer"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="code">Code *</Label>
                                <Input
                                    id="code"
                                    value={formData.code}
                                    onChange={e =>
                                        setFormData({ ...formData, code: e.target.value })
                                    }
                                    placeholder="e.g., BANK_TRANSFER"
                                />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="display_name">Display Name *</Label>
                            <Input
                                id="display_name"
                                value={formData.display_name}
                                onChange={e =>
                                    setFormData({ ...formData, display_name: e.target.value })
                                }
                                placeholder="e.g., Bank Transfer"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="description">Description</Label>
                            <Textarea
                                id="description"
                                value={formData.description}
                                onChange={e =>
                                    setFormData({ ...formData, description: e.target.value })
                                }
                                placeholder="Description of the payment method..."
                                rows={2}
                            />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="note">
                                Note for Traders
                                <span className="text-muted-foreground font-normal ml-1">
                                    (instructions or info shown to traders)
                                </span>
                            </Label>
                            <Textarea
                                id="note"
                                value={formData.note}
                                onChange={e => setFormData({ ...formData, note: e.target.value })}
                                placeholder="e.g., Bank transfer usually takes 1-3 business days..."
                                rows={2}
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="processing_time_hours">
                                    Processing Time (hours) *
                                </Label>
                                <Input
                                    id="processing_time_hours"
                                    type="number"
                                    value={formData.processing_time_hours}
                                    onChange={e =>
                                        setFormData({
                                            ...formData,
                                            processing_time_hours: parseInt(e.target.value),
                                        })
                                    }
                                    min="0"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="min_amount">Minimum Amount *</Label>
                                <Input
                                    id="min_amount"
                                    type="number"
                                    step="0.01"
                                    value={formData.min_amount}
                                    onChange={e =>
                                        setFormData({
                                            ...formData,
                                            min_amount: parseFloat(e.target.value),
                                        })
                                    }
                                    min="0"
                                />
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label htmlFor="max_amount">Maximum Amount *</Label>
                                <Input
                                    id="max_amount"
                                    type="number"
                                    step="0.01"
                                    value={formData.max_amount}
                                    onChange={e =>
                                        setFormData({
                                            ...formData,
                                            max_amount: parseFloat(e.target.value),
                                        })
                                    }
                                    min="0"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label htmlFor="fee_percentage">Fee Percentage (%) *</Label>
                                <Input
                                    id="fee_percentage"
                                    type="number"
                                    step="0.01"
                                    value={formData.fee_percentage}
                                    onChange={e =>
                                        setFormData({
                                            ...formData,
                                            fee_percentage: parseFloat(e.target.value),
                                        })
                                    }
                                    min="0"
                                    max="100"
                                />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="fee_fixed">Fixed Fee *</Label>
                            <Input
                                id="fee_fixed"
                                type="number"
                                step="0.01"
                                value={formData.fee_fixed}
                                onChange={e =>
                                    setFormData({
                                        ...formData,
                                        fee_fixed: parseFloat(e.target.value),
                                    })
                                }
                                min="0"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Method Category *</Label>
                            <Select
                                value={formData.method_category}
                                onValueChange={v =>
                                    setFormData({
                                        ...formData,
                                        method_category: v as 'fiat' | 'crypto',
                                    })
                                }
                            >
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="fiat">Fiat</SelectItem>
                                    <SelectItem value="crypto">Crypto</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="method_type">Method kind *</Label>
                            <Select
                                value={formData.method_type}
                                onValueChange={v =>
                                    setFormData({
                                        ...formData,
                                        method_type: v as PaymentMethodTypeUi,
                                        supports_processors:
                                            v === 'processor'
                                                ? formData.supports_processors
                                                : false,
                                    })
                                }
                            >
                                <SelectTrigger id="method_type">
                                    <SelectValue placeholder="Select method kind" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="processor">
                                        Processor (payment integration)
                                    </SelectItem>
                                    <SelectItem value="manual">
                                        Manual (bank / crypto / custom instructions)
                                    </SelectItem>
                                </SelectContent>
                            </Select>
                            <p className="text-xs text-muted-foreground">
                                Processor methods open the hosted payment page or widget. Manual
                                methods show your configured transfer instructions per currency.
                            </p>
                        </div>
                        {formData.method_type === 'processor' && (
                            <div className="flex items-center space-x-2">
                                <Switch
                                    id="supports_processors"
                                    checked={formData.supports_processors}
                                    onCheckedChange={checked =>
                                        setFormData({ ...formData, supports_processors: checked })
                                    }
                                />
                                <Label htmlFor="supports_processors">
                                    Enable linked payment processors
                                </Label>
                            </div>
                        )}
                        {formData.method_type === 'manual' && (
                            <div className="space-y-3 rounded-lg border p-3 max-h-72 overflow-y-auto">
                                <Label className="text-sm font-medium">
                                    Manual instructions per supported currency
                                </Label>
                                <div className="flex justify-end">
                                    <Button
                                        type="button"
                                        variant="outline"
                                        size="sm"
                                        onClick={() =>
                                            setManualRows(prev => [
                                                ...prev,
                                                {
                                                    ...defaultManualRow(),
                                                    currency_id: filteredCurrencies?.[0]?.id || '',
                                                },
                                            ])
                                        }
                                    >
                                        <Plus className="h-4 w-4" />
                                        Add instruction row
                                    </Button>
                                </div>
                                {manualRows.length === 0 ? (
                                    <p className="text-sm text-muted-foreground">
                                        Add at least one row.
                                    </p>
                                ) : (
                                    manualRows.map((row, rowIndex) => {
                                        const cur =
                                            filteredCurrencies?.find(
                                                (c: any) => c.id === row.currency_id
                                            ) ||
                                            currencies?.find((c: any) => c.id === row.currency_id);
                                        const sym = cur?.symbol || row.currency_id;
                                        return (
                                            <div
                                                key={`${row.currency_id}-${rowIndex}-${row.detail_type}`}
                                                className="space-y-2 rounded-md border bg-muted/30 p-3"
                                            >
                                                <div className="flex items-center gap-2">
                                                    <Select
                                                        value={row.currency_id}
                                                        onValueChange={v =>
                                                            setManualRows(prev =>
                                                                prev.map((r, i) =>
                                                                    i === rowIndex
                                                                        ? { ...r, currency_id: v }
                                                                        : r
                                                                )
                                                            )
                                                        }
                                                    >
                                                        <SelectTrigger className="h-9">
                                                            <SelectValue placeholder="Currency" />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            {filteredCurrencies?.map((c: any) => (
                                                                <SelectItem key={c.id} value={c.id}>
                                                                    {c.symbol}
                                                                </SelectItem>
                                                            ))}
                                                        </SelectContent>
                                                    </Select>
                                                    <Button
                                                        type="button"
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() =>
                                                            setManualRows(prev =>
                                                                prev.filter(
                                                                    (_, i) => i !== rowIndex
                                                                )
                                                            )
                                                        }
                                                    >
                                                        Remove
                                                    </Button>
                                                </div>
                                                <div className="font-medium text-sm">{sym}</div>
                                                <div className="space-y-1">
                                                    <Label className="text-xs">
                                                        Instruction type
                                                    </Label>
                                                    <Select
                                                        value={row.detail_type}
                                                        onValueChange={v =>
                                                            setManualRows(prev =>
                                                                prev.map((r, i) =>
                                                                    i === rowIndex
                                                                        ? {
                                                                              ...r,
                                                                              detail_type:
                                                                                  v as ManualDetailType,
                                                                          }
                                                                        : r
                                                                )
                                                            )
                                                        }
                                                    >
                                                        <SelectTrigger className="h-9">
                                                            <SelectValue />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                            <SelectItem value="bank">
                                                                Bank transfer
                                                            </SelectItem>
                                                            <SelectItem value="crypto">
                                                                Cryptocurrency
                                                            </SelectItem>
                                                            <SelectItem value="credit_card">
                                                                Credit card
                                                            </SelectItem>
                                                            <SelectItem value="custom">
                                                                Custom (WU / other)
                                                            </SelectItem>
                                                        </SelectContent>
                                                    </Select>
                                                </div>
                                                {row.detail_type === 'bank' && (
                                                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                                                        <Input
                                                            placeholder="Bank name *"
                                                            value={row.bank_name}
                                                            onChange={e =>
                                                                setManualRows(prev =>
                                                                    prev.map((r, i) =>
                                                                        i === rowIndex
                                                                            ? {
                                                                                  ...r,
                                                                                  bank_name:
                                                                                      e.target
                                                                                          .value,
                                                                              }
                                                                            : r
                                                                    )
                                                                )
                                                            }
                                                        />
                                                        <Input
                                                            placeholder="IBAN"
                                                            value={row.iban}
                                                            onChange={e =>
                                                                setManualRows(prev =>
                                                                    prev.map((r, i) =>
                                                                        i === rowIndex
                                                                            ? {
                                                                                  ...r,
                                                                                  iban: e.target
                                                                                      .value,
                                                                              }
                                                                            : r
                                                                    )
                                                                )
                                                            }
                                                        />
                                                        <Input
                                                            placeholder="Account number"
                                                            value={row.account_number}
                                                            onChange={e =>
                                                                setManualRows(prev =>
                                                                    prev.map((r, i) =>
                                                                        i === rowIndex
                                                                            ? {
                                                                                  ...r,
                                                                                  account_number:
                                                                                      e.target
                                                                                          .value,
                                                                              }
                                                                            : r
                                                                    )
                                                                )
                                                            }
                                                        />
                                                        <Input
                                                            placeholder="SWIFT / BIC"
                                                            value={row.swift}
                                                            onChange={e =>
                                                                setManualRows(prev =>
                                                                    prev.map((r, i) =>
                                                                        i === rowIndex
                                                                            ? {
                                                                                  ...r,
                                                                                  swift: e.target
                                                                                      .value,
                                                                              }
                                                                            : r
                                                                    )
                                                                )
                                                            }
                                                        />
                                                    </div>
                                                )}
                                                {row.detail_type === 'crypto' && (
                                                    <div className="grid grid-cols-1 gap-2">
                                                        <Input
                                                            placeholder="Wallet address *"
                                                            value={row.wallet_address}
                                                            onChange={e =>
                                                                setManualRows(prev =>
                                                                    prev.map((r, i) =>
                                                                        i === rowIndex
                                                                            ? {
                                                                                  ...r,
                                                                                  wallet_address:
                                                                                      e.target
                                                                                          .value,
                                                                              }
                                                                            : r
                                                                    )
                                                                )
                                                            }
                                                        />
                                                        <Input
                                                            placeholder="Network (e.g. ERC20)"
                                                            value={row.network}
                                                            onChange={e =>
                                                                setManualRows(prev =>
                                                                    prev.map((r, i) =>
                                                                        i === rowIndex
                                                                            ? {
                                                                                  ...r,
                                                                                  network:
                                                                                      e.target
                                                                                          .value,
                                                                              }
                                                                            : r
                                                                    )
                                                                )
                                                            }
                                                        />
                                                        <Input
                                                            placeholder="Memo / tag (optional)"
                                                            value={row.memo}
                                                            onChange={e =>
                                                                setManualRows(prev =>
                                                                    prev.map((r, i) =>
                                                                        i === rowIndex
                                                                            ? {
                                                                                  ...r,
                                                                                  memo: e.target
                                                                                      .value,
                                                                              }
                                                                            : r
                                                                    )
                                                                )
                                                            }
                                                        />
                                                    </div>
                                                )}
                                                {row.detail_type === 'credit_card' && (
                                                    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                                                        <Input
                                                            placeholder="Card holder *"
                                                            value={row.card_holder}
                                                            onChange={e =>
                                                                setManualRows(prev =>
                                                                    prev.map((r, i) =>
                                                                        i === rowIndex
                                                                            ? {
                                                                                  ...r,
                                                                                  card_holder:
                                                                                      e.target
                                                                                          .value,
                                                                              }
                                                                            : r
                                                                    )
                                                                )
                                                            }
                                                        />
                                                        <Input
                                                            placeholder="Card number *"
                                                            value={row.card_number}
                                                            onChange={e =>
                                                                setManualRows(prev =>
                                                                    prev.map((r, i) =>
                                                                        i === rowIndex
                                                                            ? {
                                                                                  ...r,
                                                                                  card_number:
                                                                                      e.target
                                                                                          .value,
                                                                              }
                                                                            : r
                                                                    )
                                                                )
                                                            }
                                                        />
                                                    </div>
                                                )}
                                                {row.detail_type === 'custom' && (
                                                    <Textarea
                                                        placeholder="Instructions for the trader *"
                                                        rows={3}
                                                        value={row.instructions}
                                                        onChange={e =>
                                                            setManualRows(prev =>
                                                                prev.map((r, i) =>
                                                                    i === rowIndex
                                                                        ? {
                                                                              ...r,
                                                                              instructions:
                                                                                  e.target.value,
                                                                          }
                                                                        : r
                                                                )
                                                            )
                                                        }
                                                    />
                                                )}
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                        )}
                        <div className="flex items-center space-x-2">
                            <Switch
                                id="requires_address"
                                checked={formData.requires_address}
                                onCheckedChange={checked =>
                                    setFormData({ ...formData, requires_address: checked })
                                }
                            />
                            <Label htmlFor="requires_address">Requires Address</Label>
                        </div>
                        <div className="flex items-center space-x-2">
                            <Switch
                                id="is_active"
                                checked={formData.is_active}
                                onCheckedChange={checked =>
                                    setFormData({ ...formData, is_active: checked })
                                }
                            />
                            <Label htmlFor="is_active">Active</Label>
                        </div>
                    </div>
                    {/* Processors section - only when editing and supports_processors */}
                    {!isCreateMode &&
                        formData.method_type === 'processor' &&
                        formData.supports_processors &&
                        selectedPaymentMethod &&
                        availableProcessors &&
                        availableProcessors.length > 0 && (
                            <div className="space-y-3 border-t pt-4">
                                <div className="flex items-center justify-between">
                                    <Label className="text-base font-medium flex items-center gap-2">
                                        <Link2 className="h-4 w-4" />
                                        Payment Processors
                                    </Label>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={handleSaveProcessors}
                                        disabled={updateProcessors.isPending}
                                    >
                                        {updateProcessors.isPending ? (
                                            <Loader2 className="h-4 w-4 animate-spin" />
                                        ) : null}
                                        Save Processors
                                    </Button>
                                </div>
                                <div className="space-y-2 max-h-40 overflow-y-auto">
                                    {availableProcessors.map(processor => {
                                        const config = processorConfigs[processor.id];
                                        const isEnabled = config?.is_enabled || false;
                                        return (
                                            <div
                                                key={processor.id}
                                                className="flex items-center justify-between rounded-lg border p-3"
                                            >
                                                <span className="font-medium">
                                                    {processor.provider_name}
                                                </span>
                                                <Switch
                                                    checked={isEnabled}
                                                    onCheckedChange={checked =>
                                                        handleToggleProcessor(processor.id, checked)
                                                    }
                                                />
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        )}
                    <DialogFooter className="flex-wrap gap-2">
                        <div className="flex gap-2 mr-auto">
                            {!isCreateMode && selectedPaymentMethod && (
                                <>
                                    <Button
                                        variant="outline"
                                        onClick={() => handleToggleStatus(selectedPaymentMethod.id)}
                                        disabled={toggleStatus.isPending}
                                    >
                                        {toggleStatus.isPending ? (
                                            <Loader2 className="h-4 w-4 animate-spin" />
                                        ) : selectedPaymentMethod.is_active ? (
                                            <PowerOff className="h-4 w-4" />
                                        ) : (
                                            <Power className="h-4 w-4" />
                                        )}
                                        {selectedPaymentMethod.is_active
                                            ? 'Deactivate'
                                            : 'Activate'}
                                    </Button>
                                    <Button
                                        variant="destructive"
                                        onClick={() => {
                                            setIsDialogOpen(false);
                                            handleDelete(selectedPaymentMethod);
                                        }}
                                    >
                                        <Trash2 className="h-4 w-4" />
                                        Delete
                                    </Button>
                                </>
                            )}
                        </div>
                        <div className="flex gap-2">
                            <Button
                                variant="outline"
                                onClick={() => {
                                    setIsDialogOpen(false);
                                    setSelectedPaymentMethod(null);
                                    setFormData(initialFormData);
                                }}
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={handleSubmit}
                                disabled={
                                    createPaymentMethod.isPending || updatePaymentMethod.isPending
                                }
                            >
                                {createPaymentMethod.isPending || updatePaymentMethod.isPending ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                ) : null}
                                {isCreateMode ? 'Create' : 'Update'}
                            </Button>
                        </div>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Delete Payment Method</DialogTitle>
                        <DialogDescription>
                            Are you sure you want to delete "{selectedPaymentMethod?.display_name}"?
                            This action cannot be undone.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setIsDeleteDialogOpen(false)}>
                            Cancel
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={handleConfirmDelete}
                            disabled={deletePaymentMethod.isPending}
                        >
                            {deletePaymentMethod.isPending ? (
                                <RefreshCw className="h-4 w-4 animate-spin" />
                            ) : (
                                <Trash2 className="h-4 w-4" />
                            )}
                            Delete
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </PageContainer>
    );
}
